import { Logger } from '@frontend/logger';

import { AuthenticationProvider } from './authentication-provider';
import { BasicAuthenticationClient } from './basic-authentication-client';
import { Token } from './models';
import { RefreshHelper } from './refresh-helper';
import { TokenRefreshScheduler } from './token-refresh-scheduler';

const KEEP_LOGGED_IN_KEY = 'keep_logged_in';
const SSO_KEEP_LOGGED_IN_KEY = 'sso_keep_logged_in';

export class AuthenticationManager extends AuthenticationProvider {
    private static instance: AuthenticationManager | null;
    private readonly refreshScheduler = new TokenRefreshScheduler();

    private constructor() {
        super();
        RefreshHelper.getInstance().setTokenSaver((token) => this.saveTokenToStorage(token));
        this.initializeAutoRefresh();
    }

    public static getInstance = (): AuthenticationManager => {
        if (!AuthenticationManager.instance) {
            AuthenticationManager.instance = new AuthenticationManager();
        }
        return AuthenticationManager.instance;
    };

    isKeepLoggedInEnabled(): boolean {
        return localStorage.getItem(KEEP_LOGGED_IN_KEY) === 'true';
    }

    setKeepLoggedIn(enabled: boolean): void {
        localStorage.setItem(KEEP_LOGGED_IN_KEY, String(enabled));
        if (!enabled) {
            this.refreshScheduler.clear();
        }
    }

    prepareSsoLogin(keepLoggedIn: boolean): void {
        sessionStorage.setItem(SSO_KEEP_LOGGED_IN_KEY, String(keepLoggedIn));
    }

    async authenticate(email: string, password: string, keepLoggedIn = false): Promise<Token> {
        this.setKeepLoggedIn(keepLoggedIn);
        const credentials = await BasicAuthenticationClient.login(email, password);

        this.saveTokenToStorage(credentials);
        Logger.log(credentials);
        this.loggedIn = true;
        return credentials;
    }

    async ssoAuthenticate(accountId: string, providerId: string, code: string): Promise<Token> {
        this.setKeepLoggedIn(this.consumeSsoKeepLoggedIn());
        const credentials = await BasicAuthenticationClient.ssoLogin(accountId, providerId, code);

        this.saveTokenToStorage(credentials);
        Logger.log(credentials);
        this.loggedIn = true;
        return credentials;
    }

    async refresh(): Promise<void> {
        return RefreshHelper.getInstance().refresh();
    }

    async doLogout() {
        try {
            const token = await this.waitForToken();
            await BasicAuthenticationClient.logout(token.jwt_token);
            this.logout(() => {
                if (token.identity_provider === 'microsoft') {
                    window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${window.location.origin}`;
                } else window.location.href = '/';
            });
            this.loggedIn = false;
        } catch (error) {
            Logger.error('Something went wrong trying to logout.', error);
        }
    }

    async authorizeContext(entityType: string, entityId: string): Promise<void> {
        const token = await this.waitForToken();
        const newToken = await BasicAuthenticationClient.authorizeContext(token.jwt_token, {
            entity_type: entityType,
            entity_id: entityId
        });
        this.saveTokenToStorage(newToken);
    }

    async deAuthorizeContext(): Promise<void> {
        const token = await this.waitForToken();
        const newToken = await BasicAuthenticationClient.deAuthorizeContext(token.jwt_token);
        this.saveTokenToStorage(newToken);
    }

    async switchAccount(accountId: string): Promise<Token> {
        const token = await this.waitForToken();
        const newToken = await BasicAuthenticationClient.switchAccount(token.jwt_token, accountId);
        this.saveTokenToStorage(newToken);
        return newToken;
    }

    async syncContextWithSpot(spotId: string | null): Promise<void> {
        const token = await this.waitForToken();
        const currentContextId = token.context?.entity_id ?? null;

        if (spotId && currentContextId !== spotId) {
            await this.authorizeContext('spot', spotId);
        } else if (!spotId && token.context !== null) {
            await this.deAuthorizeContext();
        }
    }

    async signUp(email: string, password: string, password_confirmation: string, url: string): Promise<any> {
        try {
            const response = await BasicAuthenticationClient.signUp(email, password, password_confirmation, url);
            this.loggedIn = true;
            return response;
        } catch (err) {
            Logger.error('Sign up failed.', err);
            throw err;
        }
    }

    async confirmSignUp(token: string): Promise<{ type: string; message: string }> {
        try {
            const credentials = await BasicAuthenticationClient.confirmSignUp(token);
            if ((credentials as any).detail) throw new Error((credentials as any).detail);
            this.saveTokenToStorage(credentials);
            Logger.log(credentials);
            this.loggedIn = true;
            return { type: 'success', message: 'Sign up confirmed! You will be redirected to the main page' };
        } catch (err) {
            return { type: 'error', message: 'Sign up confirmation failed. Please try again.' };
        }
    }

    async forgotPassword(email: string, url: string): Promise<{ message: string }> {
        const message = 'An email with a link to reset your password has been sent to you';
        try {
            await BasicAuthenticationClient.forgotPassword(email, url);
            return { message: message };
        } catch (err) {
            return { message: message };
        }
    }

    async confirmForgotPassword(token: string, newPassword: string): Promise<{ type: string; message: string }> {
        try {
            const response = await BasicAuthenticationClient.changeForgottenPassword(token, newPassword);
            if ((response as any).detail) throw new Error((response as any).detail);
            Logger.log(response);
            return { type: 'success', message: 'Password change confirmed! You will be redirected to the login page' };
        } catch (err) {
            return {
                type: 'error',
                message: 'Unable to change your password with this confirmation code. Please try again or request a new password reset.'
            };
        }
    }

    override logout(callback?: () => any): void {
        this.refreshScheduler.clear();
        super.logout(callback);
    }

    protected override saveTokenToStorage(token: Token): void {
        super.saveTokenToStorage(token);
        if (this.isKeepLoggedInEnabled()) {
            this.scheduleTokenRefresh(token);
        } else {
            this.refreshScheduler.clear();
        }
    }

    private initializeAutoRefresh(): void {
        if (!this.isKeepLoggedInEnabled() || !this.hasValidToken()) {
            return;
        }

        const token = this.getTokenFromStorage();
        if (token) {
            this.scheduleTokenRefresh(token);
        }
    }

    private scheduleTokenRefresh(token: Token): void {
        this.refreshScheduler.schedule(token, () => this.refreshScheduledToken(), 'Session');
    }

    private async refreshScheduledToken(): Promise<void> {
        if (!this.isKeepLoggedInEnabled()) {
            return;
        }

        Logger.log('Session token expiring soon, refreshing...');
        try {
            await this.refresh();
        } catch (error) {
            Logger.error('Failed to refresh session token:', error);
        }
    }

    private consumeSsoKeepLoggedIn(): boolean {
        const value = sessionStorage.getItem(SSO_KEEP_LOGGED_IN_KEY);
        sessionStorage.removeItem(SSO_KEEP_LOGGED_IN_KEY);
        return value === 'true';
    }
}
